import { ReloadOutlined } from "@ant-design/icons";
import { useAuthFetch, useUser } from "@p0security/frontend-core";
import { useFirestoreDoc } from "@p0security/frontend-core/react";
import { PermissionRequest } from "@p0security/shared/types/permission";
import {
  ActiveRequestStatuses,
  PendingRequestStatuses,
} from "@p0security/shared/types/request-status";
import { Drawer, Flex } from "@p0security/ui";
import { isa } from "@p0security/util/types/is";
import { useNavigateWithEnv } from "components/Assessment/hooks/useNavigateWithEnv";
import { GraphTooltip } from "components/GraphTooltip";
import { RequestAccessModalButton } from "components/Jit/Modal/RequestAccessModalButton";
import { Tenant } from "components/Login";
import { useCallback, useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { handleRequestActionResponse, isGrantHolder } from "../requestUtils";
import { ActiveRequestActions } from "./ActiveRequestActions";
import { ApproveRequestModal } from "./ApproveRequestModal";
import { PendingRequestActions } from "./PendingRequestActions";
import { RequestLogs } from "./RequestLogs";

export const RequestDetailDrawer: React.FC<{ requestId: string }> = ({
  requestId,
}) => {
  const tenantId = useContext(Tenant);
  const location = useLocation();
  const navigate = useNavigateWithEnv();
  const { user } = useUser();

  const [requestProcessing, setRequestProcessing] = useState(false);
  const authFetch = useAuthFetch(undefined, setRequestProcessing);

  const { doc, loading } = useFirestoreDoc<PermissionRequest>(
    `o/${tenantId}/permission-requests/${requestId}`,
    { live: true }
  );
  const request = useMemo(() => doc?.data, [doc]);
  const isPending = isa(PendingRequestStatuses, request?.status ?? "");
  const isActive = isa(ActiveRequestStatuses, request?.status ?? "");
  // "Request for me" files a brand-new request for the viewer, prefilled from
  // this one (the modal carries over neither principal nor requestor), so it is
  // offered on any request in the table — including other people's. Hide it only
  // when the viewer already holds this grant — i.e. is the active request's
  // principal (the "Relinquish" case) — where requesting it again for themselves
  // is meaningless. Requests can be filed on behalf of another principal, so this
  // keys off the grant holder, not the requestor: it stays available for an
  // active grant held by someone else (the "Revoke" case) and for non-active
  // requests.
  const holdsGrant = isActive && isGrantHolder(user, request);

  const closeDrawer = useCallback(
    () => navigate({ ...location, pathname: ".." }, { relative: "path" }),
    [location, navigate]
  );

  const [approveModalOpen, setApproveModalOpen] = useState(false);

  const onApprove = useCallback(() => {
    setApproveModalOpen(true);
  }, []);
  const onDeny = useCallback(async () => {
    handleRequestActionResponse(
      await authFetch(`permission-requests/${requestId}/deny`, {
        method: "POST",
      }),
      "Request denied"
    );
  }, [requestId, authFetch]);

  const onRevoke = useCallback(async () => {
    handleRequestActionResponse(
      await authFetch(`permission-requests/${requestId}/revoke`, {
        method: "POST",
      }),
      "Access revoked"
    );
  }, [requestId, authFetch]);

  const closeModal = useCallback(() => {
    setApproveModalOpen(false);
  }, []);

  return (
    <Drawer
      title="Request Details"
      width="75%"
      open
      onClose={closeDrawer}
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Flex align="baseline" gap="0.5em" justify="space-between" wrap>
          {isPending ? (
            PendingRequestActions(onApprove, onDeny)
          ) : isActive ? (
            <ActiveRequestActions request={doc} onRevoke={onRevoke} />
          ) : null}
          {request && !holdsGrant && (
            <GraphTooltip title="Create a new access request for yourself, prefilled with this request's details">
              <RequestAccessModalButton
                requestId={requestId}
                type="default"
                variant="outlined"
                icon={<ReloadOutlined />}
              >
                Request for me
              </RequestAccessModalButton>
            </GraphTooltip>
          )}
        </Flex>
      }
    >
      <RequestLogs requestId={requestId} tenantId={tenantId} />
      <ApproveRequestModal
        request={approveModalOpen ? doc : undefined}
        loading={loading || requestProcessing}
        onClose={closeModal}
      />
    </Drawer>
  );
};
